import { useEffect, useRef } from 'react';

/**
 * Global keyboard shortcuts, minus the ways they usually go wrong: they never
 * fire while somebody is typing, and they never fire on a browser chord
 * (Cmd-R must still reload the page).
 */

export type HotkeyMap = Record<string, (event: KeyboardEvent) => void>;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable
  );
}

/**
 * Keys are described as `shift+r`, `space`, `enter`, `escape`, `k`.
 * Only shift is allowed as a modifier: anything with meta/ctrl/alt belongs to
 * the browser.
 */
export function useHotkeys(map: HotkeyMap, enabled = true): void {
  const mapRef = useRef(map);
  mapRef.current = map;

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      const key = event.key === ' ' ? 'space' : event.key.toLowerCase();
      const combo = event.shiftKey ? `shift+${key}` : key;
      const handler = mapRef.current[combo];
      if (!handler) return;

      event.preventDefault();
      handler(event);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
